import { BaseWebComponentView } from '../../../../framework/BaseWebComponentView';
import { CompanyData, InstallAgentViewData } from '../types';
import { GzButtonWebComponent } from '../../../../ui/gzButton';
import { translateFn } from '../../../../utils/translator.helper';
import { TemplateState } from '../../../../types/common/types';
import { OnInstallAgentViewInit } from '../events/InstallAgentPanelEvents';
import { GzFormFieldWebComponent } from '../../../../ui/gzForm/formField';
import { GzDropdownWebComponent } from '../../../../ui/gzDropdown';
import { ListPosition } from '../../../../ui/gzDropdown/types';
import { GzTitleWebComponent } from '../../../../ui/gzTitle';
import { LanguageData } from '../../../../packages/types/language-data';
import { FormGroup } from '../../../../ui/gzForm/formGroup/form-group';
import { PackageModulesFormWebComponent } from '../../../../packages/common/packageModulesForm';
import {
  WEB_COMPONENT_PACKAGE_DEPLOYER_FORM,
  WEB_COMPONENT_INSTALL_AGENT_ADDITIONAL_TARGETS,
  WEB_COMPONENT_PACKAGE_MISCELLANEOUS_FORM,
  WEB_COMPONENT_PACKAGE_MODULES_FORM,
  WEB_COMPONENT_PACKAGE_SCAN_MODES_FORM,
  WEB_COMPONENT_PACKAGE_SECURITY_SERVER_ASSIGNMENT_GRID,
  WEB_COMPONENT_PACKAGE_SETTINGS_FORM,
  WEB_COMPONENT_PACKAGE_USE_PROXY_FORM,
  WEB_COMPONENT_RECONFIGURE_AGENT_SCHEDULER_FORM,
  WEB_COMPONENT_CREDENTIALS_MANAGER_GRID,
} from '../../../../webcomponents.constants';
import { PackageScanModesFormWebComponent } from '../../../../packages/common/packageScanModesForm';
import { PackageSecurityServerAssignmentGridWebComponent } from '../../../../packages/common/packageSecurityServerAssignmentGrid';
import { PackageMiscellaneousFormWebComponent } from '../../../../packages/common/packageMiscellaneousForm';
import { PackageSettingsFormWebComponent } from '../../../../packages/common/packageSettingsForm';
import { GzInfoBoxWebComponent } from '../../../../ui/gzInfoBox';
import { htmlEncode } from '../../../../utils/escape.utils';
import { InstallAgentAdditionalTargetsFormWebComponent } from '../../common/installAgentAdditionalTargetsForm';
import { GzCheckboxWebComponent } from '../../../../ui/gzCheckbox';
import { GzInputWebComponent } from '../../../../ui/gzInput/component/GzInputWebComponent';
import { PackageSummaryData } from '../../../../utils/response';
import { RequiredValidator } from '../../../../ui/gzForm/validators/required.validator';
import { MaxLengthValidator } from '../../../../ui/gzForm/validators/max-length.validator';
import { PackageConstants } from '../../../../packages/PackageConstants';
import { RemoteValidator } from '../../../../ui/gzForm/validators/remote.validator';
import { RegexValidator } from '../../../../ui/gzForm/validators/regex.validator';
import { CustomFieldsFormConstants } from '../../../../company/common/customFieldsForm/CustomFieldsFormConstants';
import { PackageType } from '../../../../packages/packagesGrid/types';
import { ReconfigureAgentSchedulerFormWebComponent } from '../../../reconfigureAgent/common/reconfigureAgentShedulerForm';
import { toggleVisibility } from '../../../../utils/htmlElement.utils';
import { PackageDeployerFormWebComponent } from '../../../../packages/common/packageDeployerForm';
import { PackageUseProxyFormWebComponent } from '../../../../packages/common/packageUseProxyForm';
import { CustomizableValidator } from '../../../../ui/gzForm/validators/customizable.validator';
import { ValidationResult } from '../../../../ui/gzForm/formField/types/form-validator';
import { CONNECT_TO_DEPLOYER_OPTIONS, DeployType } from '../../../../packages/common/packageDeployerForm/types';
import { CredentialsManagerGridWebComponent } from '../../common/credentialsManagerGrid';
import { LinkColor, LinkType } from '../../../../ui/gzLinkComponent/types';

export class InstallAgentPanelView extends BaseWebComponentView {
  public formGroup!: FormGroup;
  private _customPackage = false;
  private _company: CompanyData;

  get customPackage(): boolean {
    return this._customPackage;
  }

  set customPackage(isCustomPackage: boolean) {
    this._customPackage = isCustomPackage;
  }

  get company(): CompanyData {
    return this._company;
  }

  set company(value: CompanyData) {
    this._company = value;
  }

  public dom: {
    $wrapper: HTMLElement;

    $optionsFormSubtitle: GzTitleWebComponent;
    $optionsSchedulerForm: ReconfigureAgentSchedulerFormWebComponent;
    $automaticallyRebootCheckbox: GzFormFieldWebComponent<GzCheckboxWebComponent, boolean>;
    $automaticallyRebootInfo: GzInfoBoxWebComponent;

    $installCredentialManagerFormSubtitle: GzTitleWebComponent;
    $installCredentialManagerGrid: CredentialsManagerGridWebComponent;

    $installAgentDeployerFormSubtitle: GzTitleWebComponent;
    $installAgentDeployerForm: PackageDeployerFormWebComponent;

    $installAgentAdditionalTargetsFormField: InstallAgentAdditionalTargetsFormWebComponent;
    $packageFormField: GzFormFieldWebComponent<GzDropdownWebComponent, string | number>;
    $customizePackageInfo: GzInfoBoxWebComponent;

    $customizePackageButton: GzButtonWebComponent;
    $packageWrapper: HTMLElement;
    $generalDetailsSubtitle: GzTitleWebComponent;

    $languageFormField: GzFormFieldWebComponent<GzDropdownWebComponent, string | number>;
    $packageModulesForm: PackageModulesFormWebComponent;
    $packageScanModesForm: PackageScanModesFormWebComponent;
    $packageSecurityServerAssignmentForm: PackageSecurityServerAssignmentGridWebComponent;
    $packageMiscellaneousForm: PackageMiscellaneousFormWebComponent;
    $packageSettingsForm: PackageSettingsFormWebComponent;
    $packageUseProxyForm: PackageUseProxyFormWebComponent;
    $saveAsPackageCheckbox: GzFormFieldWebComponent<GzCheckboxWebComponent, boolean>;

    $saveAsPackageFormField: GzFormFieldWebComponent<GzInputWebComponent, string>;
    $removeCompetitorInfo: GzInfoBoxWebComponent;

    $saveButton: GzButtonWebComponent;
    $cancelButton: GzButtonWebComponent;
  };

  getDomElements(): void {
    this.dom = {
      $wrapper: this.shadow.querySelector('.wrapper'),

      $optionsFormSubtitle: this.shadow.querySelector('#options-form-subtitle'),
      $optionsSchedulerForm: this.shadow.querySelector(WEB_COMPONENT_RECONFIGURE_AGENT_SCHEDULER_FORM),
      $automaticallyRebootCheckbox: this.shadow.querySelector('#automatically-reboot-checkbox'),

      $installCredentialManagerFormSubtitle: this.shadow.querySelector('#credentials-manager-form-subtitle'),
      $installCredentialManagerGrid: this.shadow.querySelector(WEB_COMPONENT_CREDENTIALS_MANAGER_GRID),

      $installAgentDeployerFormSubtitle: this.shadow.querySelector('#install-deployer-form-subtitle'),
      $installAgentDeployerForm: this.shadow.querySelector(WEB_COMPONENT_PACKAGE_DEPLOYER_FORM),

      $installAgentAdditionalTargetsFormField: this.shadow.querySelector(
        WEB_COMPONENT_INSTALL_AGENT_ADDITIONAL_TARGETS
      ),
      $packageFormField: this.shadow.querySelector('#package-form-field'),
      $customizePackageButton: this.shadow.querySelector('#customize-package-button'),
      $customizePackageInfo: this.shadow.querySelector('#customize-package-info'),

      $packageWrapper: this.shadow.querySelector('#package-wrapper'),
      $generalDetailsSubtitle: this.shadow.querySelector('#general-details-subtitle'),
      $languageFormField: this.shadow.querySelector('#language-form-field'),

      $packageModulesForm: this.shadow.querySelector(WEB_COMPONENT_PACKAGE_MODULES_FORM),
      $packageScanModesForm: this.shadow.querySelector(WEB_COMPONENT_PACKAGE_SCAN_MODES_FORM),
      $packageSecurityServerAssignmentForm: this.shadow.querySelector(
        WEB_COMPONENT_PACKAGE_SECURITY_SERVER_ASSIGNMENT_GRID
      ),
      $packageMiscellaneousForm: this.shadow.querySelector(WEB_COMPONENT_PACKAGE_MISCELLANEOUS_FORM),
      $packageSettingsForm: this.shadow.querySelector(WEB_COMPONENT_PACKAGE_SETTINGS_FORM),
      $packageUseProxyForm: this.shadow.querySelector(WEB_COMPONENT_PACKAGE_USE_PROXY_FORM),
      $removeCompetitorInfo: this.shadow.querySelector('#remove-competitor-info'),
      $automaticallyRebootInfo: this.shadow.querySelector('#automatically-reboot-info'),
      $saveAsPackageCheckbox: this.shadow.querySelector('#save-as-package-checkbox'),
      $saveAsPackageFormField: this.shadow.querySelector('#save-as-package-form-field'),

      $saveButton: this.shadow.querySelector('#save-button'),
      $cancelButton: this.shadow.querySelector('#cancel-button'),
    };

    this.setLoadingState();
    this.getTranslations();
    this.setTranslatedStrings();
  }

  async buildSuccessView(viewData: InstallAgentViewData): Promise<void> {
    this.company = viewData.company;
    this.dom.$optionsSchedulerForm.injectDependencies(this.dependencies);

    this.dom.$installAgentDeployerForm.setCompanyId(viewData.company.id);
    this.dom.$installAgentDeployerForm.isInstallAgentDeployer = true;
    this.dom.$installAgentDeployerForm.injectDependencies(this.dependencies);
    this.dom.$installCredentialManagerGrid.injectDependencies(this.dependencies);

    const startWebCompPromises: Promise<void>[] = [
      this.dom.$optionsSchedulerForm.start(),
      this.dom.$installAgentDeployerForm.start(),
      this.dom.$installCredentialManagerGrid.start(),
    ];

    Promise.all(startWebCompPromises)
      .then(async () => {
        this.dom.$wrapper.dispatchEvent(new OnInstallAgentViewInit());
        this.removeLoadingState();
      })
      .catch(console.error);

    if (!this.dependencies.isCloud()) {
      await this.handleAdditionalTargetsVisibility(viewData.hasNoCompanySelected);
    }
    this.createFormGroup();

    this.setPackageDropdownOptions(viewData.companyPackages);
    this.setLanguageDropdownOptions(viewData.languages);
  }

  getTranslations(): void {
    this.commonStrings = this.stringsTranslator.getCommonStrings();
  }

  setTranslatedStrings(): void {
    this.dom.$optionsFormSubtitle.innerText = translateFn('Options');
    this.dom.$automaticallyRebootCheckbox.Input.label = translateFn('Automatically reboot (if needed)');

    this.dom.$installCredentialManagerFormSubtitle.innerText = translateFn('Credentials Manager');
    this.dom.$installAgentDeployerFormSubtitle.innerText = translateFn('Deployer');

    this.dom.$packageFormField.label = translateFn('Use Package');
    this.dom.$packageFormField.Input.placeholder = translateFn('Select package');
    this.dom.$packageFormField.Input.noResultsFound = this.commonStrings.noResultsFound;
    this.dom.$customizePackageButton.innerText = translateFn('Customize');
    this.dom.$customizePackageInfo.innerText = translateFn('An error occurred while processing your request.');

    this.dom.$generalDetailsSubtitle.innerText = this.dependencies.translateFn('General');
    this.dom.$languageFormField.label = translateFn('Language');
    this.dom.$languageFormField.Input.placeholder = translateFn('Select the language');
    this.dom.$languageFormField.Input.noResultsFound = this.commonStrings.noResultsFound;

    let removeCompetitorInfoLink =
      'https://www.bitdefender.com/business/support/en/77212-376350-software-incompatible-with-best.html';
    if (this.dependencies.isCloud()) {
      removeCompetitorInfoLink =
        'https://www.bitdefender.com/business/support/en/77209-376350-software-incompatible-with-best.html';
      if (this.dependencies.isPartner()) {
        removeCompetitorInfoLink =
          'https://www.bitdefender.com/business/support/en/77211-376350-software-incompatible-with-best.html';
      }
    }

    this.dom.$removeCompetitorInfo.innerHTML = `${htmlEncode(
      this.dependencies.translateFn('BEST will automatically uninstall other')
    )} <gz-link class='security-software-link-info' color="${LinkColor.SECONDARY}" type="${LinkType.UNDERLINE}" href='${removeCompetitorInfoLink}'>
    <span>${htmlEncode(this.dependencies.translateFn('security software.'))}</span></gz-link>`;
    this.dom.$automaticallyRebootInfo.innerHTML = translateFn(
      'When the automatic reboot option is disabled, the endpoint will remain unprotected until the next manual reboot.'
    );

    this.dom.$saveAsPackageCheckbox.Input.label = translateFn('Save as package');
    this.dom.$saveAsPackageFormField.Input.placeholder = translateFn('Type name');

    this.dom.$saveButton.innerText = this.commonStrings.save;
    this.dom.$cancelButton.innerText = translateFn('Cancel');
  }

  public setLoadingState(): void {
    this.resetTemplateState(this.dom.$wrapper);
    this.setTemplateState(this.dom.$wrapper, TemplateState.LOADING);
  }

  public removeLoadingState(): void {
    this.resetTemplateState(this.dom.$wrapper);
  }

  public handleAutomaticallyRebootInfoVisibility(visibility: boolean): void {
    toggleVisibility(this.dom.$automaticallyRebootInfo, visibility);
  }

  buildErrorView(error: Error): void {
    this.resetTemplateState(this.dom.$wrapper);
    this.setTemplateState(this.dom.$wrapper, TemplateState.ERROR);
  }

  private createFormGroup(): void {
    this.setPackageNameValidators();
    this.dom.$packageFormField.Validators = [
      RequiredValidator(this.commonStrings.requiredField),
      CustomizableValidator(this.customValidationPackageRelay.bind(this)),
    ];
    this.dom.$saveAsPackageFormField.Validators = [RequiredValidator(this.commonStrings.requiredField)];

    this.formGroup = FormGroup.build({
      settings: {
        newPackageName: this.dom.$saveAsPackageFormField,
        saveAsPackage: this.dom.$saveAsPackageCheckbox,
        specifics: {
          settings: {
            language: this.dom.$languageFormField,
          },
        },
      },
    });
  }

  private customValidationPackageRelay(): ValidationResult {
    const packageData: object | undefined = this.dom.$packageFormField.Input.selectedOption?.data;
    const errorMessage = translateFn('A relay cannot be deployed through another relay.');

    if (this.customPackage) {
      return {
        valid: true,
      };
    }

    if (
      packageData &&
      packageData['settings']['modules']['updateServer'] &&
      this.dom.$installAgentDeployerForm.deployerType ===
        CONNECT_TO_DEPLOYER_OPTIONS[DeployType.EndpointSecurityRelay].value
    ) {
      return {
        valid: false,
        error: errorMessage,
      };
    }

    return {
      valid: true,
    };
  }

  private setPackageNameValidators(): void {
    this.dom.$saveAsPackageFormField.Validators = [
      RequiredValidator(this.commonStrings.requiredField),
      MaxLengthValidator(
        PackageConstants.PackageName.MaxLength,
        translateFn('The name must not be longer than {packageNameMinLength} characters in length', {
          packageNameMinLength: PackageConstants.PackageName.MaxLength,
        })
      ),
      RemoteValidator(
        (value: string) =>
          this.dependencies.api.clients.Package.CheckNameIsUnique(value, null, PackageType.Endpoints, this.company.id),
        (response: { success: boolean }) => response.success,
        translateFn('Package with this name already exists'),
        translateFn('A communication error has occurred. Please enter the package name again')
      ),
      RegexValidator(
        CustomFieldsFormConstants.RegexValueForCustomFields,
        translateFn('The name contains characters that are not allowed')
      ),
    ];
  }

  private setPackageDropdownOptions(packages: PackageSummaryData[]): void {
    this.dom.$packageFormField.Input.options = packages.map(packageData => ({
      title: packageData.name,
      value: packageData._id,
      data: packageData,
    }));
    this.dom.$packageFormField.Input.listPosition = ListPosition.TOP;
  }

  private setLanguageDropdownOptions(languages: LanguageData[]): void {
    this.dom.$languageFormField.Input.options = languages.map((language: LanguageData) => {
      return {
        title: language.title,
        value: language.id,
      };
    });
  }

  private async handleAdditionalTargetsVisibility(hasNoCompanySelected: boolean): Promise<void> {
    this.dom.$installAgentAdditionalTargetsFormField.hasNoCompanySelected = hasNoCompanySelected;
    this.dom.$installAgentAdditionalTargetsFormField.injectDependencies(this.dependencies);
    await this.dom.$installAgentAdditionalTargetsFormField.start();
  }

  public toggleVisibilityRemoveCompetitorInfo(isVisible: boolean): void {
    toggleVisibility(this.dom.$removeCompetitorInfo, isVisible);
  }
}
